/*  ========================================================================
    # Sections - Projects
    ========================================================================  */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PostCard, PostCardSkeleton } from '../../components/PostCard';
import bem from '../../helpers/bem';
import useLocalization from '../../hooks/useLocalization';
import useHistoryValues from '../../hooks/useHistoryValues';
import useTranslation from '../../hooks/useTranslation';
import usePostsApi from '../../hooks/usePostsApi';
import useGetParams from '../../hooks/useGetParams';
import AnimateHeight from 'react-animate-height';
import tailwindConfig from '../../../../tailwind.config';
import NoEntries from '../../components/NoEntries';

const getCategoryIdBySlug = (slug, categories) => {
  const category = categories.find(cat => cat.slug === slug);
  if (category) return category.cat_ID;
  return '';
};

const getCategorySlugById = (id, categories) => {
  const category = categories.find(cat => cat.cat_ID === parseInt(id, 10));
  if (category) return category.slug;
  return undefined;
};

const getCategoryIdsFromParams = (defaults, categories) => {
  let defaultCats = []; // eslint-disable-line
  if (defaults) {
    const defaultsArray = defaults.split(',');
    defaultsArray.forEach(element => {
      defaultCats.push(getCategoryIdBySlug(element, categories));
    });
  }
  return defaultCats;
};

const Posts = ({
  showCta,
  showCategories,
  showSearch,
  heading,
  postType,
  showAllEntries,
  entriesCount,
  categories,
  selectedPosts
}) => {
  const __ = useTranslation();
  const parameterCategory = __('param-category');
  const getParams = useGetParams();

  // Icons
  const local = useLocalization();
  const iconSearch = `${local('svgSprite')}#search`;

  // Posts
  const [renderedPosts, setRenderedPosts] = useState([]);
  const [postsOffset, setPostsOffset] = useState(0);
  let perPage;

  if (showAllEntries) {
    perPage = 9;
  } else if (
    selectedPosts !== null &&
    selectedPosts.length > 0 &&
    entriesCount >= selectedPosts.length
  ) {
    perPage = entriesCount - selectedPosts.length;
  } else {
    perPage = entriesCount;
  }

  const [showFilter, setShowFilter] = useState(false);
  const [filterHeight, setFilterHeight] = useState(0);

  const getReadingTime = str => {
    const arr = str?.split(' ');

    return Math.ceil(arr.filter(word => word !== '').length / 200);
  };

  // Categories
  const defaultCategories = getCategoryIdsFromParams(getParams[parameterCategory], categories);
  const [selectedCategories, setSelectedCategories] = useState(defaultCategories);

  const pushNewValues = useHistoryValues({
    [parameterCategory]: selectedCategories
  });

  const getCategoryNameById = id => {
    const category = categories.find(cat => cat.cat_ID === parseInt(id, 10));
    if (category) {
      return category.name;
    }
    return '';
  };

  const handleCategoryChange = catID => {
    const newCatsSlug = [];
    if (selectedCategories.find(category => category === catID)) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories([catID]);
      // Push new Cats with slug to history params
      newCatsSlug.push(getCategorySlugById(catID, categories));
    }

    setPostsOffset(0);
    setRenderedPosts([]);

    pushNewValues({
      [parameterCategory]: newCatsSlug
    });
  };

  // Search
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const exclude = selectedPosts?.map(entry => entry.post.ID);

  // Call PostAPI
  const { posts, loadingPosts, postsCount } = usePostsApi(postType, exclude, {
    per_page: perPage,
    offset: postsOffset,
    search: searchQuery,
    categories: selectedCategories
  });

  // Handle load more
  const loadMorePosts = () => {
    setPostsOffset(renderedPosts.length);
  };

  // Update search
  const updateSearchQuery = () => {
    setPostsOffset(0);
    setRenderedPosts([]);
    setSearchQuery(searchInput);
  };

  // Get Post info
  const getPostInfo = post => {
    if (post.post.post_type === 'post') {
      const content = post.content ? post.content : post.post.post_content;
      return __('section-posts-post-info', {
        category: getCategoryNameById(post.info),
        minutes: getReadingTime(content)
      });
    }
    return post.info;
  };

  useEffect(() => {
    setRenderedPosts(prev => [...prev, ...posts]);
  }, [posts]);

  // general useEffect
  useEffect(() => {
    // set the initial posts
    if (selectedPosts) setRenderedPosts(prev => [...prev, ...selectedPosts]);

    // Handler updates show filter for window sizes.
    const handler = () => {
      if (window.innerWidth > parseInt(tailwindConfig.theme.screens.xl, 10)) {
        setShowFilter(true);
      }
    };

    // Add event listener.
    window.addEventListener('resize', handler, { passive: true });

    // Call it initially.
    handler();

    // On component unmount, remove event listener.
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (showFilter) setFilterHeight('auto');
    else setFilterHeight(0);
  }, [showFilter]);

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  return (
    <>
      <div className='container'>
        {(heading || showCategories || showSearch) && (
          <div className='tw-mb-11'>
            {(heading || (postType !== 'posts' && showSearch)) && (
              <div className={bem('s-posts', 'top', { 'filter-active': showCategories })}>
                {heading && !showCategories && !showSearch && (
                  <div className='wysiwyg-block' dangerouslySetInnerHTML={{ __html: heading }} />
                )}
                {heading && (showCategories || showSearch) && (
                  <div
                    className='wysiwyg-block'
                    style={{ flex: 1 }}
                    dangerouslySetInnerHTML={{ __html: heading }}
                  ></div>
                )}
                {heading && showCategories && (
                  <button
                    title='Filter'
                    onClick={toggleFilter}
                    className={
                      bem('s-posts', 'filter-button') +
                      ` ` +
                      bem('c-button', '', { 'outline-black': !showFilter, black: showFilter })
                    }
                    dangerouslySetInnerHTML={{ __html: 'Filter' }}
                  ></button>
                )}
                {!showCategories && showSearch && (
                  <form
                    className={bem('s-posts', 'search')}
                    onSubmit={event => {
                      event.preventDefault();
                      updateSearchQuery();
                    }}
                  >
                    <input
                      id='posts-search-input'
                      type='search'
                      placeholder={__('section-posts-search-placeholder')}
                      value={searchInput}
                      onChange={e => setSearchInput(e.target.value)}
                    />
                    <label htmlFor='posts-search-input'>{__('section-posts-search-label')}</label>
                    <button type='submit' title={__('section-posts-search-submit')}>
                      <svg viewBox='0 0 200 200'>
                        <use href={iconSearch} />
                      </svg>
                    </button>
                  </form>
                )}
              </div>
            )}
            {showCategories && postType === 'posts' && (
              <AnimateHeight height={filterHeight} duration={250} delay={0}>
                <div className={bem('s-posts', 'filter')}>
                  <div className={bem('s-posts', 'categories')}>
                    {categories.map(cat => (
                      <button
                        key={cat.cat_ID}
                        title={cat.name}
                        className={bem('c-button', '', {
                          black: selectedCategories.find(el => el === cat.cat_ID),
                          'outline-black': !selectedCategories.find(el => el === cat.cat_ID)
                        })}
                        onClick={() => handleCategoryChange(cat.cat_ID)}
                        dangerouslySetInnerHTML={{ __html: cat.name }}
                      />
                    ))}
                  </div>
                  {showSearch && (
                    <form
                      className={bem('s-posts', 'search')}
                      onSubmit={event => {
                        event.preventDefault();
                        updateSearchQuery();
                      }}
                    >
                      <input
                        id='posts-search-input'
                        type='search'
                        placeholder={__('section-posts-search-placeholder')}
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                      />
                      <label htmlFor='posts-search-input'>{__('section-posts-search-label')}</label>
                      <button type='submit' title={__('section-posts-search-submit')}>
                        <svg viewBox='0 0 200 200'>
                          <use href={iconSearch} />
                        </svg>
                      </button>
                    </form>
                  )}
                </div>
              </AnimateHeight>
            )}
          </div>
        )}
      </div>

      <div className='container'>
        <div
          className={bem('s-posts', 'entries', {
            'three-elements': parseInt(entriesCount, 10) === 3
          })}
        >
          {renderedPosts.map((post, index) => (
            <PostCard
              key={`${post.title}_${index}`}
              style={{ order: index }}
              {...post}
              info={getPostInfo(post)}
            />
          ))}
          {showCta && renderedPosts.length > 0 && (
            <div className={bem('s-posts', 'cta')}>
              <h4>{__('stay-informed')}</h4>
              <a
                className={bem('c-button', '', {
                  black: true
                })}
                href='/newsletter'
              >
                {__('subscribe-newsletter')}
              </a>
            </div>
          )}
          {loadingPosts &&
            [...Array(perPage)].map((value, index) => <PostCardSkeleton key={index} />)}
        </div>
        {renderedPosts.length === 0 && !loadingPosts && (
          <NoEntries text={__('section-posts-no-entries-text')} />
        )}
        {postsCount > renderedPosts.length && showAllEntries && (
          <div className={bem('s-posts', 'load-more')}>
            <button
              type='button'
              className={bem('c-button', '', {
                black: true
              })}
              onClick={loadMorePosts}
            >
              {__('section-posts-load-more')}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

Posts.propTypes = {
  showCta: PropTypes.bool,
  showCategories: PropTypes.bool,
  showSearch: PropTypes.bool,
  heading: PropTypes.string,
  postType: PropTypes.string,
  showAllEntries: PropTypes.bool,
  entriesCount: PropTypes.string,
  categories: PropTypes.array,
  selectedPosts: PropTypes.array
};

export default Posts;
